import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminReportsApi } from '@api/client';
import type { ReportByCenterRow, ReportByDayRow, ReportRange } from '@api/types';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { todayInTimezone } from '@lib/timezone';

const RANGES: readonly { value: ReportRange; label: string }[] = [
  { value: '30d', label: '30 días' },
  { value: '90d', label: '90 días' },
  { value: 'ytd', label: 'Año' },
  { value: 'all', label: 'Todo' },
];

function downloadCsv(filename: string, rows: readonly (readonly (string | number)[])[]) {
  const csv = rows
    .map((row) => row.map((cell) => {
      const s = String(cell);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(','))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function InformesPage() {
  const [range, setRange] = useState<ReportRange>('90d');

  const { data: byCenter, isLoading: loadingCenter } = useQuery<ReportByCenterRow[]>({
    queryKey: ['reports', 'by-center', range],
    queryFn: () => adminReportsApi.byCenter(range),
    staleTime: 60_000,
  });

  const { data: byDay, isLoading: loadingDay } = useQuery<ReportByDayRow[]>({
    queryKey: ['reports', 'by-day', range],
    queryFn: () => adminReportsApi.byDay(range),
    staleTime: 60_000,
  });

  const totals = useMemo(() => {
    const init = { bookings: 0, completed: 0, cancelled: 0, noShow: 0, pendingApproval: 0 };
    return (byCenter ?? []).reduce((acc, c) => ({
      bookings:        acc.bookings        + c.bookings,
      completed:       acc.completed       + c.completed,
      cancelled:       acc.cancelled       + c.cancelled,
      noShow:          acc.noShow          + c.noShow,
      pendingApproval: acc.pendingApproval + c.pendingApproval,
    }), init);
  }, [byCenter]);

  const maxBookingsCenter = Math.max(1, ...(byCenter ?? []).map((c) => c.bookings));
  const maxBookingsDay    = Math.max(1, ...(byDay    ?? []).map((d) => d.bookings));

  const completionRate = totals.bookings > 0 ? totals.completed / totals.bookings : 0;
  const noShowRate     = totals.bookings > 0 ? totals.noShow    / totals.bookings : 0;
  const cancelRate     = totals.bookings > 0 ? totals.cancelled / totals.bookings : 0;

  // M-9 closeout: el filename del CSV debe reflejar el día calendárico
  // español (admin es ES-only por diseño). Antes UTC del navegador
  // marcaba 1 día menos en exports nocturnos.
  const stampToday = () => todayInTimezone('Europe/Madrid');

  function exportCentersCsv() {
    const headerRow = ['Centro', 'Reservas', 'Completadas', 'Canceladas', 'No-show', 'Pend. aprobación'];
    const rows: (string | number)[][] = [headerRow];
    for (const c of byCenter ?? []) {
      rows.push([c.name, c.bookings, c.completed, c.cancelled, c.noShow, c.pendingApproval]);
    }
    downloadCsv(`informes-centros-${range}-${stampToday()}.csv`, rows);
  }

  function exportDailyCsv() {
    const headerRow = ['Fecha', 'Reservas'];
    const rows: (string | number)[][] = [headerRow, ...(byDay ?? []).map((d) => [d.date, d.bookings])];
    downloadCsv(`informes-diario-${range}-${stampToday()}.csv`, rows);
  }

  // Status distribution for bar list (uses totals)
  const statusBars = [
    { label: 'Completadas',         count: totals.completed,       tone: 'ok'      },
    { label: 'Canceladas',          count: totals.cancelled,       tone: 'danger'  },
    { label: 'No-show',             count: totals.noShow,          tone: 'danger'  },
    { label: 'Pend. aprobación',    count: totals.pendingApproval, tone: 'warn'    },
  ];
  const maxStatusBar = Math.max(1, ...statusBars.map((s) => s.count));

  const isLoading = loadingCenter || loadingDay;
  const hasData = (byCenter?.length ?? 0) > 0 || (byDay?.length ?? 0) > 0;

  return (
    <div>
      <PageHead
        title="Informes"
        sub="KPIs agregados, comparativa por centro y exportación CSV. Solo superadmin."
        actions={
          <>
            <div className="seg">
              {RANGES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  className="seg-btn"
                  data-active={range === r.value}
                  onClick={() => { setRange(r.value); }}
                >{r.label}</button>
              ))}
            </div>
            <button className="btn" onClick={exportCentersCsv} disabled={!byCenter?.length}>
              <Icons.Download size={14} /> CSV centros
            </button>
          </>
        }
      />

      <div className="stat-row">
        <div className="stat">
          <div className="stat-label">Reservas totales</div>
          <div className="stat-value mono">{totals.bookings.toLocaleString('es-ES')}</div>
          <div className="stat-delta" data-tone="neutral">{byCenter?.length ?? 0} centros activos</div>
        </div>
        <div className="stat">
          <div className="stat-label">Tasa de finalización</div>
          <div className="stat-value mono">{Math.round(completionRate * 100)}%</div>
          <div className="stat-delta" data-tone="neutral">{totals.completed.toLocaleString('es-ES')} completadas</div>
        </div>
        <div className="stat">
          <div className="stat-label">No-show</div>
          <div className="stat-value mono">{(noShowRate * 100).toFixed(1)}%</div>
          <div className="stat-delta" data-tone="neutral">{totals.noShow} sin presentarse</div>
        </div>
        <div className="stat">
          <div className="stat-label">Cancelación</div>
          <div className="stat-value mono">{(cancelRate * 100).toFixed(1)}%</div>
          <div className="stat-delta" data-tone="neutral">{totals.cancelled} canceladas</div>
        </div>
      </div>

      {isLoading && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}><span className="spinner" /></div>
      )}

      {!isLoading && !hasData && (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)' }}>
          Sin datos en el rango seleccionado.
        </div>
      )}

      {!isLoading && hasData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 18, marginBottom: 18 }}>
            {/* Bar chart: reservas por día */}
            <div className="card">
              <div className="card-head">
                <span className="card-title">Reservas por día</span>
                <button className="btn" data-size="sm" onClick={exportDailyCsv}>
                  <Icons.Download size={12} /> CSV
                </button>
              </div>
              <div style={{ padding: 22, display: 'flex', alignItems: 'flex-end', gap: 4, height: 220, borderBottom: '1px solid var(--border)' }}>
                {(byDay ?? []).map((d) => {
                  const h = Math.max(4, (d.bookings / maxBookingsDay) * 180);
                  return (
                    <div
                      key={d.date}
                      title={`${d.date}: ${String(d.bookings)} reservas`}
                      style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 6 }}
                    >
                      <div className="mono" style={{ fontSize: 9.5, color: 'var(--fg-soft)' }}>{d.bookings}</div>
                      <div style={{
                        width: '100%',
                        height: h,
                        background: 'var(--accent)',
                        borderRadius: '3px 3px 0 0',
                        opacity: 0.9,
                      }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: '8px 22px 14px', display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-soft)' }}>
                <span>{(byDay ?? [])[0]?.date.slice(5) ?? ''}</span>
                <span>{(byDay ?? [])[(byDay ?? []).length - 1]?.date.slice(5) ?? ''}</span>
              </div>
            </div>

            {/* Status distribution */}
            <div className="card">
              <div className="card-head">
                <span className="card-title">Distribución por estado</span>
              </div>
              <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {statusBars.map((s) => (
                  <div key={s.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                      <span>{s.label}</span>
                      <span className="mono cell-muted">{s.count}</span>
                    </div>
                    <div style={{ height: 5, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
                      <div
                        data-tone={s.tone}
                        style={{
                          width: `${String((s.count / maxStatusBar) * 100)}%`,
                          height: '100%',
                          background: s.tone === 'ok' ? 'var(--accent)'
                            : s.tone === 'warn' ? 'oklch(0.62 0.13 70)'
                            : 'var(--danger)',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Comparativa por centro */}
          <div className="card">
            <div className="card-head">
              <span className="card-title">Comparativa por centro</span>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Centro</th>
                  <th>Volumen</th>
                  <th className="cell-right">Reservas</th>
                  <th className="cell-right">Completadas</th>
                  <th className="cell-right">Canceladas</th>
                  <th className="cell-right">No-show</th>
                  <th className="cell-right">Pend. aprob.</th>
                </tr>
              </thead>
              <tbody>
                {(byCenter ?? []).map((c) => (
                  <tr key={c.centerId}>
                    <td className="cell-bold">{c.name}</td>
                    <td style={{ width: '28%' }}>
                      <div style={{ height: 5, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${String((c.bookings / maxBookingsCenter) * 100)}%`, height: '100%', background: 'var(--accent)' }} />
                      </div>
                    </td>
                    <td className="cell-right mono">{c.bookings.toLocaleString('es-ES')}</td>
                    <td className="cell-right mono">{c.completed.toLocaleString('es-ES')}</td>
                    <td className="cell-right mono">{c.cancelled}</td>
                    <td className="cell-right mono">{c.noShow}</td>
                    <td className="cell-right mono">{c.pendingApproval}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
